import React, { createContext, useContext, ReactNode, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "./AuthProvider";
import Gleap from "gleap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMessage } from "@fortawesome/free-solid-svg-icons";

interface GleapContextType {
  openGleap: () => void;
}

const GleapContext = createContext<GleapContextType | undefined>(undefined);

interface GleapProviderProps {
  children: ReactNode;
}

export const GleapProvider: React.FC<GleapProviderProps> = ({ children }) => {
  const auth = useContext(AuthContext);
  const user = auth?.user;
  const location = useLocation();
  const initializedRef = useRef(false);
  const hideFab =
    location.pathname.startsWith("/auth") ||
    location.pathname.startsWith("/reset");

  const openGleap = () => {
    if (!initializedRef.current && user?.id) {
      Gleap.initialize(process.env.REACT_APP_GLEAP_API_KEY as string);

      Gleap.identify(user?.code || String(user.id), {
        name: user?.name,
        email: user?.email,
      });

      Gleap.showFeedbackButton(false);

      initializedRef.current = true;
    }

    Gleap.startClassicForm("bugreporting", true);
  };

  return (
    <GleapContext.Provider value={{ openGleap }}>
      {children}

      {!hideFab ? (
        <button
          onClick={openGleap}
          className="fixed bottom-4 right-4 z-[30] flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-gradient-to-br from-accent to-accent-bright text-lg text-white shadow-glow transition-all duration-200 hover:scale-105 hover:shadow-overlay sm:bottom-6 sm:right-6 sm:h-14 sm:w-14 sm:text-xl"
          title="Report a Bug"
        >
          <FontAwesomeIcon icon={faMessage} />
        </button>
      ) : null}
    </GleapContext.Provider>
  );
};

export const useGleap = (): GleapContextType => {
  const context = useContext(GleapContext);
  if (!context) {
    throw new Error("useGleap must be used within a GleapProvider");
  }
  return context;
};
